import { Controller } from "@hotwired/stimulus"

// Fallbacks for the server-side copy passed in through the `translations`
// value; a key the view did not send never reads as `undefined`.
const DEFAULT_TRANSLATIONS = {
  // Localized "We switched the number series to the default for this
  // document type" toast — set by the template via
  // `data-number-series--type-sync-translations-value="…"`.
  toastMessage: ""
}

// Connects to data-controller="number-series--type-sync"
//
// Watches a document_type <select> (via a `change` action) and keeps the page
// in sync with the picked type:
//   1. Rewrites the `?document_type=` query param on the number-series-picker
//      modal link so a future picker open filters to the new type's allowed
//      number-series.
//   2. Fetches the resolved default number-series for that type from
  //      `GET /:workspace_id/default_number_series_for/:document_type` and writes
//      the result into the existing number-series--sanitizer targets in the
//      main form:
//        - `numberSeriesId`    (hidden field carrying number_series_id on submit)
//        - `numberSeriesInput` (visible number/mask field)
//
// Why a dedicated controller (not part of document-generator--form):
// `document-generator--form` handles the generator sidebar's own state
// (scheduling, email, end-condition). This controller crosses sidebar →
// main-form boundary AND makes a network request — different blast radius
// from its neighbors. Stack both on the sidebar root via
// `data-controller="document-generator--form number-series--type-sync"`.
export default class extends Controller {
  static values = {
    translations: { type: Object, default: {} },
    workspaceId: Number,
    // True for recurring-document templates — the endpoint returns the mask
    // (not a generated number) so the visible field shows the pattern that
    // generated documents will follow.
    isRecurring: { type: Boolean, default: false }
  }

  get #texts() {
    return { ...DEFAULT_TRANSLATIONS, ...this.translationsValue }
  }

  documentTypeChanged(event) {
    const documentType = event.target.value
    this.#syncPickerLink(documentType)
    this.#applyDefaultNumberSeries(documentType)
  }

  #syncPickerLink(documentType) {
    const link = document.getElementById("number_series_picker_link")
    if (!link) return
    const url = new URL(link.href)
    url.searchParams.set("document_type", documentType)
    link.href = url.toString()
  }

  async #applyDefaultNumberSeries(documentType) {
    if (!this.workspaceIdValue || !documentType) return

    const numberSeriesIdField = document.querySelector('[data-number-series--sanitizer-target="numberSeriesId"]')
    const numberField = document.querySelector('[data-number-series--sanitizer-target="numberSeriesInput"]')
    if (!numberSeriesIdField) return

    // Race-condition guard — only the latest request applies its result.
    // Rapid type-flips (keyboard arrow scrubbing through the select) could
    // otherwise let a stale response overwrite a fresh one.
    const requestId = ++this.#latestRequest
    // Route helper: default_number_series_for_workspace_path(workspace) →
    //   /:workspace_id/default_number_series_for/:document_type (no `/workspaces/` prefix).
    const url = `/${this.workspaceIdValue}/default_number_series_for/${documentType}?is_recurring=${this.isRecurringValue}`

    try {
      const response = await fetch(url, { headers: { Accept: "application/json" } })
      if (!response.ok) {
        console.warn(`number-series-type-sync: ${response.status} from ${url}`)
        return
      }
      // First staleness check — skip JSON parsing entirely if a newer request
      // already raced past us during `fetch`.
      if (requestId !== this.#latestRequest) return

      const data = await response.json()
      // Second staleness check — `response.json()` is itself async, so a newer
      // request can overtake between the two awaits. Without this guard, a
      // slow JSON parse could let stale data clobber a freshly-applied value.
      if (requestId !== this.#latestRequest) return
      if (!data.number_series_id) return

      const previousSeriesId = numberSeriesIdField.value
      numberSeriesIdField.value = data.number_series_id
      numberSeriesIdField.dispatchEvent(new Event("change", { bubbles: true }))

      if (numberField && data.number != null) {
        numberField.value = data.number
        numberField.dispatchEvent(new Event("input", { bubbles: true }))
      }

      // Only toast when the controller actually swapped the series —
      // re-applying the same default after a flip-flop would be noise.
      if (previousSeriesId !== String(data.number_series_id)) {
        this.#showSwitchedToast()
      }
    } catch (err) {
      console.error("number-series-type-sync: failed to apply default number-series:", err)
    }
  }

  // Drop a dismissible alert into the page's flash container so users
  // notice that picking a different document_type also rotated the
  // number-series. The alert markup mirrors `_flash.html.erb` so theme
  // styles apply without extra CSS.
  #showSwitchedToast() {
    const message = this.#texts.toastMessage
    if (!message) return
    const container = document.querySelector(".flash-messages")
    if (!container) return

    const alert = document.createElement("div")
    alert.className = "alert alert--info alert-dismissible fade show"
    alert.setAttribute("role", "status")
    alert.innerHTML = `
			<span class="material-symbols-outlined">info</span>
			<span></span>
			<button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
		`
    alert.querySelector("span:nth-of-type(2)").textContent = message
    container.appendChild(alert)
  }

  #latestRequest = 0
}
